/**
 * Modal fullscreen para crear/editar MovimientoInventario
 * Réplica del modal de MovimientosBodegaPage con todas sus funcionalidades
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import bodegaService from '../services/bodegaService';
import { recursoService, presupuestoCostoService, proyectoService } from '../services/onebaseService';
import presupuestoHierarchyService from '../services/presupuestoHierarchyService';
import api from '../services/api';
import '../pages/MovimientosBodegaPage.css';

const CrearMovimientoBodegaModal = ({ proyecto, movimientoEditando, onClose, onCreated }) => {
  const modoEdicion = !!movimientoEditando;
  const [loading, setLoading] = useState(false);
  const [bodegas, setBodegas] = useState([]);
  const [recursos, setRecursos] = useState([]);

  // Estados para presupuesto
  const [proyectos, setProyectos] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [partidas, setPartidas] = useState([]);
  const [recursosPresupuestarios, setRecursosPresupuestarios] = useState([]);

  // Formulario principal
  const [formData, setFormData] = useState({
    tipo_movimiento: 'salida',
    bodega: '',
    bodega_destino: '',
    motivo: 'uso_interno',
    descripcion: '',
    tipo_documento: '',
    numero_documento: '',
    estado: 'borrador'
  });

  // Detalles del movimiento
  const [detalles, setDetalles] = useState([]);

  // Detalle temporal para agregar
  const [detalleTemp, setDetalleTemp] = useState({
    recurso: '',
    cantidad: 0,
    precio_unitario: 0,
    observaciones: '',
    proyecto: '',
    presupuesto_costo: '',
    area: '',
    partida: '',
    presupuesto_costo_recurso: ''
  });

  // Estado para edición de detalles
  const [editingDetalleIndex, setEditingDetalleIndex] = useState(null);

  // Stock disponible
  const [stockDisponible, setStockDisponible] = useState(null);

  useEffect(() => {
    cargarDatosIniciales();
  }, []);

  useEffect(() => {
    if (modoEdicion && movimientoEditando) {
      cargarMovimientoParaEditar();
    }
  }, [modoEdicion, movimientoEditando]);

  const cargarDatosIniciales = async () => {
    try {
      setLoading(true);
      const [bodegasRes, recursosRes, proyectosRes] = await Promise.all([
        bodegaService.getBodegas(),
        recursoService.getAll({ stock: true }),
        proyectoService.getAll()
      ]);

      setBodegas(bodegasRes.data.results || bodegasRes.data || []);
      setRecursos(recursosRes.data.results || recursosRes.data || []);
      setProyectos(proyectosRes.data.results || proyectosRes.data || []);

      // Si hay proyecto pre-seleccionado, cargar sus presupuestos
      if (proyecto && !modoEdicion) {
        const presupuestosRes = await presupuestoCostoService.getAll({ proyecto: proyecto.id, status: 'activo' });
        const presupuestosData = presupuestosRes.data.results || presupuestosRes.data || [];
        setPresupuestos(presupuestosData);

        // Pre-seleccionar proyecto
        setDetalleTemp(prev => ({ ...prev, proyecto: proyecto.id.toString() }));
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar datos iniciales');
    } finally {
      setLoading(false);
    }
  };

  const cargarMovimientoParaEditar = async () => {
    try {
      setLoading(true);
      const res = await bodegaService.getMovimiento(movimientoEditando.id);
      const movimiento = res.data;

      // Cargar datos del formulario principal
      setFormData({
        tipo_movimiento: movimiento.tipo_movimiento || 'salida',
        bodega: movimiento.bodega || '',
        bodega_destino: movimiento.bodega_destino || '',
        motivo: movimiento.motivo || 'uso_interno',
        descripcion: movimiento.descripcion || '',
        tipo_documento: movimiento.tipo_documento || '',
        numero_documento: movimiento.numero_documento || '',
        estado: movimiento.estado || 'completado'
      });

      // Cargar detalles si existen
      if (movimiento.detalles && movimiento.detalles.length > 0) {
        const detallesCargados = movimiento.detalles.map(d => ({
          id: d.id,
          recurso: d.recurso,
          recurso_nombre: d.recurso_nombre || '',
          recurso_unidad: d.recurso_unidad || '',
          cantidad: parseFloat(d.cantidad || 0),
          precio_unitario: parseFloat(d.precio_unitario || 0),
          observaciones: d.observaciones || '',
          proyecto: d.proyecto || '',
          presupuesto_costo: d.presupuesto_costo || '',
          area: d.area || '',
          partida: d.partida || '',
          presupuesto_costo_recurso: d.presupuesto_costo_recurso || '',
          presupuesto_costo_recurso_nombre: d.presupuesto_costo_recurso_nombre || '',
          proyecto_nombre: d.proyecto_nombre || ''
        }));
        setDetalles(detallesCargados);
      }
    } catch (error) {
      console.error('Error al cargar movimiento:', error);
      toast.error('Error al cargar datos del movimiento');
    } finally {
      setLoading(false);
    }
  };

  // Cargar presupuestos al seleccionar proyecto
  const handleProyectoChange = async (proyectoId) => {
    setDetalleTemp({
      ...detalleTemp,
      proyecto: proyectoId,
      presupuesto_costo: '',
      area: '',
      partida: '',
      presupuesto_costo_recurso: ''
    });
    setPresupuestos([]);
    setAreas([]);
    setPartidas([]);
    setRecursosPresupuestarios([]);

    if (proyectoId) {
      try {
        const res = await presupuestoCostoService.getAll({ proyecto: proyectoId, status: 'activo' });
        setPresupuestos(res.data.results || res.data);
      } catch (error) {
        console.error('Error al cargar presupuestos:', error);
      }
    }
  };

  // Cargar áreas al seleccionar presupuesto
  const handlePresupuestoChange = async (presupuestoId) => {
    setDetalleTemp({
      ...detalleTemp,
      presupuesto_costo: presupuestoId,
      area: '',
      partida: '',
      presupuesto_costo_recurso: ''
    });
    setAreas([]);
    setPartidas([]);
    setRecursosPresupuestarios([]);

    if (presupuestoId) {
      try {
        const areasRes = await presupuestoHierarchyService.getCostoAreasForPresupuesto(presupuestoId);
        const areasData = areasRes.data.results || areasRes.data || [];
        setAreas(areasData);
      } catch (error) {
        console.error('Error al cargar áreas:', error);
      }
    }
  };

  // Cargar partidas al seleccionar área
  const handleAreaChange = async (areaId) => {
    setDetalleTemp({
      ...detalleTemp,
      area: areaId,
      partida: '',
      presupuesto_costo_recurso: ''
    });
    setPartidas([]);
    setRecursosPresupuestarios([]);

    if (areaId) {
      try {
        const area = areas.find(a => a.id === parseInt(areaId));
        if (area && area.partidas_estructura) {
          setPartidas(area.partidas_estructura);
        }
      } catch (error) {
        console.error('Error al cargar partidas:', error);
      }
    }
  };

  // Cargar recursos presupuestarios al seleccionar partida
  const handlePartidaChange = (partidaId) => {
    setDetalleTemp({
      ...detalleTemp,
      partida: partidaId,
      presupuesto_costo_recurso: ''
    });
    setRecursosPresupuestarios([]);

    if (partidaId) {
      const partida = partidas.find(p => p.id === parseInt(partidaId));
      if (partida && partida.recursos_estructura) {
        setRecursosPresupuestarios(partida.recursos_estructura);
      }
    }
  };

  // Cargar precio del stock cuando se selecciona un recurso en una salida
  const handleRecursoChange = async (recursoId) => {
    setDetalleTemp({...detalleTemp, recurso: recursoId});
    setStockDisponible(null);

    // Si es una salida, obtener el precio y cantidad disponible del stock
    if (formData.tipo_movimiento === 'salida' && recursoId && formData.bodega) {
      try {
        const res = await bodegaService.getStocks({
          bodega: formData.bodega,
          recurso: recursoId
        });

        const stocks = res.data.results || res.data;
        if (stocks.length > 0) {
          const stock = stocks[0];
          setDetalleTemp(prev => ({
            ...prev,
            recurso: recursoId,
            precio_unitario: stock.precio_costo_unitario || 0
          }));
          setStockDisponible(parseFloat(stock.cantidad_actual || 0));
        } else {
          setStockDisponible(0);
        }
      } catch (error) {
        console.error('Error al cargar precio del stock:', error);
        setStockDisponible(0);
      }
    } else {
      setStockDisponible(null);
    }
  };

  // Manejar cambio de cantidad con validación en tiempo real
  const handleCantidadChange = (valor) => {
    const cantidad = parseFloat(valor) || 0;
    setDetalleTemp({...detalleTemp, cantidad: valor});

    if (formData.tipo_movimiento === 'salida' && stockDisponible !== null) {
      if (cantidad > stockDisponible) {
        console.warn(`Cantidad solicitada (${cantidad}) excede stock disponible (${stockDisponible})`);
      }
    }
  };

  const agregarDetalle = () => {
    if (!detalleTemp.recurso || detalleTemp.cantidad <= 0) {
      toast.error('Debe seleccionar un recurso y una cantidad válida');
      return;
    }

    // Validar stock disponible para salidas
    if (formData.tipo_movimiento === 'salida' && stockDisponible !== null) {
      const cantidadSolicitada = parseFloat(detalleTemp.cantidad);
      if (cantidadSolicitada > stockDisponible) {
        toast.error(`Stock insuficiente. Disponible: ${stockDisponible.toFixed(2)}, Solicitado: ${cantidadSolicitada.toFixed(2)}`);
        return;
      }
    }

    // Validar presupuesto_costo_recurso para salidas
    if (formData.tipo_movimiento === 'salida' && !detalleTemp.presupuesto_costo_recurso) {
      toast.error('Para movimientos de salida, debe seleccionar un recurso presupuestario');
      return;
    }

    const recursoSeleccionado = recursos.find(r => r.id === parseInt(detalleTemp.recurso));
    const recursoPresupuestarioSeleccionado = recursosPresupuestarios.find(
      rp => rp.id === parseInt(detalleTemp.presupuesto_costo_recurso)
    );
    const proyectoSeleccionado = proyectos.find(p => p.id === parseInt(detalleTemp.proyecto));

    if (editingDetalleIndex !== null) {
      // Modo edición: actualizar el detalle existente
      const nuevosDetalles = [...detalles];
      nuevosDetalles[editingDetalleIndex] = {
        ...detalleTemp,
        recurso_nombre: recursoSeleccionado?.nombre || '',
        recurso_unidad: recursoSeleccionado?.unidad || '',
        presupuesto_costo_recurso_nombre: recursoPresupuestarioSeleccionado?.nombre || '',
        proyecto_nombre: proyectoSeleccionado?.nombre || ''
      };
      setDetalles(nuevosDetalles);
      setEditingDetalleIndex(null);
      toast.success('Recurso actualizado');
    } else {
      // Modo agregar: añadir nuevo detalle
      setDetalles([...detalles, {
        ...detalleTemp,
        recurso_nombre: recursoSeleccionado?.nombre || '',
        recurso_unidad: recursoSeleccionado?.unidad || '',
        presupuesto_costo_recurso_nombre: recursoPresupuestarioSeleccionado?.nombre || '',
        proyecto_nombre: proyectoSeleccionado?.nombre || ''
      }]);
      toast.success('Recurso agregado');
    }

    setDetalleTemp({
      recurso: '',
      cantidad: 0,
      precio_unitario: 0,
      observaciones: '',
      proyecto: proyecto ? proyecto.id.toString() : detalleTemp.proyecto,
      presupuesto_costo: '',
      area: '',
      partida: '',
      presupuesto_costo_recurso: ''
    });
    setStockDisponible(null);
  };

  const editarDetalle = (index) => {
    const detalle = detalles[index];
    setDetalleTemp({
      recurso: detalle.recurso,
      cantidad: detalle.cantidad,
      precio_unitario: detalle.precio_unitario,
      observaciones: detalle.observaciones,
      proyecto: detalle.proyecto || '',
      presupuesto_costo: detalle.presupuesto_costo || '',
      area: detalle.area || '',
      partida: detalle.partida || '',
      presupuesto_costo_recurso: detalle.presupuesto_costo_recurso || ''
    });
    setEditingDetalleIndex(index);

    // Cargar datos en cascada si hay valores
    if (detalle.proyecto) {
      handleProyectoChange(detalle.proyecto);
    }
    if (detalle.presupuesto_costo) {
      handlePresupuestoChange(detalle.presupuesto_costo);
    }
    if (detalle.area) {
      handleAreaChange(detalle.area);
    }
    if (detalle.partida) {
      handlePartidaChange(detalle.partida);
    }
  };

  const eliminarDetalle = (index) => {
    setDetalles(detalles.filter((_, i) => i !== index));
    if (editingDetalleIndex === index) {
      cancelarEdicion();
    } else if (editingDetalleIndex !== null && editingDetalleIndex > index) {
      setEditingDetalleIndex(editingDetalleIndex - 1);
    }
    toast.success('Recurso eliminado');
  };

  const cancelarEdicion = () => {
    setEditingDetalleIndex(null);
    setDetalleTemp({
      recurso: '',
      cantidad: 0,
      precio_unitario: 0,
      observaciones: '',
      proyecto: proyecto ? proyecto.id.toString() : '',
      presupuesto_costo: '',
      area: '',
      partida: '',
      presupuesto_costo_recurso: ''
    });
    setStockDisponible(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (detalles.length === 0) {
      toast.error('Debe agregar al menos un recurso al movimiento');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        ...formData,
        detalles_data: detalles.map(d => ({
          id: d.id,
          recurso: d.recurso,
          cantidad: parseFloat(d.cantidad),
          precio_unitario: parseFloat(d.precio_unitario) || 0,
          observaciones: d.observaciones || '',
          presupuesto_costo_recurso: d.presupuesto_costo_recurso || null
        }))
      };

      if (modoEdicion) {
        await bodegaService.updateMovimiento(movimientoEditando.id, payload);
        toast.success('Movimiento actualizado exitosamente');
      } else {
        await bodegaService.createMovimiento(payload);
        toast.success('Movimiento registrado exitosamente');
      }

      if (onCreated) {
        onCreated();
      }

      onClose();
    } catch (error) {
      console.error(`Error al ${modoEdicion ? 'actualizar' : 'registrar'} movimiento:`, error);

      // Extraer errores de la respuesta
      const errorData = error.response?.data || {};

      // Función para mostrar errores (puede ser string o array)
      const mostrarErrores = (errores) => {
        if (Array.isArray(errores)) {
          errores.forEach(err => toast.error(err));
        } else if (typeof errores === 'string') {
          toast.error(errores);
        }
      };

      // Manejar diferentes tipos de errores
      if (errorData.non_field_errors) {
        mostrarErrores(errorData.non_field_errors);
      } else if (errorData.detalles_data) {
        mostrarErrores(errorData.detalles_data);
      } else if (errorData.detail) {
        mostrarErrores(errorData.detail);
      } else {
        Object.entries(errorData).forEach(([field, errors]) => {
          if (Array.isArray(errors)) {
            errors.forEach(err => toast.error(`${field}: ${err}`));
          } else if (typeof errors === 'string') {
            toast.error(`${field}: ${errors}`);
          } else if (typeof errors === 'object' && errors !== null) {
            toast.error(JSON.stringify(errors));
          }
        });

        if (Object.keys(errorData).length === 0) {
          toast.error(`Error al ${modoEdicion ? 'actualizar' : 'registrar'} movimiento`);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay-fullscreen" onClick={onClose}>
      <div className="modal-fullscreen" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-fullscreen">
          <div className="header-info">
            <h2>{modoEdicion ? '✏️ Editar Movimiento' : '➕ Nuevo Movimiento'}</h2>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-fullscreen-form">
          <div className="tab-content-area">
            {/* Información General */}
            <h3>Información General</h3>
            <div className="form-grid-5cols">
              <div className="form-group">
                <label>Tipo de Movimiento *</label>
                <select
                  value={formData.tipo_movimiento}
                  onChange={(e) => setFormData({...formData, tipo_movimiento: e.target.value})}
                  required
                >
                  <option value="entrada">Entrada</option>
                  <option value="salida">Salida</option>
                  <option value="ajuste">Ajuste</option>
                  <option value="traspaso">Traspaso</option>
                </select>
              </div>

              <div className="form-group">
                <label>Bodega Origen *</label>
                <select
                  value={formData.bodega}
                  onChange={(e) => setFormData({...formData, bodega: e.target.value})}
                  required
                >
                  <option value="">Seleccionar...</option>
                  {bodegas.map(b => (
                    <option key={b.id} value={b.id}>{b.nombre}</option>
                  ))}
                </select>
              </div>

              {formData.tipo_movimiento === 'traspaso' && (
                <div className="form-group">
                  <label>Bodega Destino *</label>
                  <select
                    value={formData.bodega_destino}
                    onChange={(e) => setFormData({...formData, bodega_destino: e.target.value})}
                    required
                  >
                    <option value="">Seleccionar...</option>
                    {bodegas.filter(b => b.id !== parseInt(formData.bodega)).map(b => (
                      <option key={b.id} value={b.id}>{b.nombre}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Motivo *</label>
                <select
                  value={formData.motivo}
                  onChange={(e) => setFormData({...formData, motivo: e.target.value})}
                  required
                >
                  <option value="compra">Compra</option>
                  <option value="devolucion">Devolución</option>
                  <option value="produccion">Producción</option>
                  <option value="venta">Venta</option>
                  <option value="uso_interno">Uso Interno</option>
                  <option value="merma">Merma</option>
                  <option value="ajuste_inventario">Ajuste de Inventario</option>
                  <option value="traspaso_bodega">Traspaso entre Bodegas</option>
                  <option value="otro">Otro</option>
                </select>
              </div>

              <div className="form-group">
                <label>Tipo de Documento</label>
                <select
                  value={formData.tipo_documento}
                  onChange={(e) => setFormData({...formData, tipo_documento: e.target.value})}
                >
                  <option value="">Seleccionar...</option>
                  <option value="factura">Factura</option>
                  <option value="orden_compra">Orden de compra</option>
                  <option value="guia_despacho">Guía de despacho</option>
                  <option value="boleta">Boleta</option>
                </select>
              </div>

              <div className="form-group">
                <label>Nº Documento</label>
                <input
                  type="text"
                  value={formData.numero_documento}
                  onChange={(e) => setFormData({...formData, numero_documento: e.target.value})}
                  placeholder="Factura, OC, Guía, etc."
                />
              </div>

              <div className="form-group" style={{gridColumn: '1 / -1'}}>
                <label>Descripción General</label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                  rows={2}
                  placeholder="Descripción general del movimiento"
                />
              </div>
            </div>

            {/* Recursos del Movimiento */}
            <hr style={{margin: '20px 0'}} />
            <h3>Recursos del Movimiento</h3>

            <div className="form-grid-3cols">
              <div className="form-group">
                <label>Recurso *</label>
                <select
                  value={detalleTemp.recurso}
                  onChange={(e) => handleRecursoChange(e.target.value)}
                >
                  <option value="">Seleccionar...</option>
                  {recursos.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.nombre} ({r.unidad})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Cantidad *</label>
                <input
                  type="number"
                  step="0.01"
                  value={detalleTemp.cantidad}
                  onChange={(e) => handleCantidadChange(e.target.value)}
                  placeholder="0.00"
                  style={
                    formData.tipo_movimiento === 'salida' &&
                    stockDisponible !== null &&
                    parseFloat(detalleTemp.cantidad || 0) > stockDisponible
                      ? {borderColor: '#dc2626', backgroundColor: '#fee2e2'}
                      : {}
                  }
                />
                {formData.tipo_movimiento === 'salida' && stockDisponible !== null && (
                  <small style={{
                    display: 'block',
                    marginTop: '4px',
                    color: parseFloat(detalleTemp.cantidad || 0) > stockDisponible ? '#dc2626' : '#059669',
                    fontWeight: '500'
                  }}>
                    Stock disponible: {stockDisponible.toFixed(2)}
                    {parseFloat(detalleTemp.cantidad || 0) > stockDisponible && ' ⚠️ Cantidad excede stock'}
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Precio Unitario {formData.tipo_movimiento === 'salida' && '(PPP)'}</label>
                <input
                  type="number"
                  step="0.01"
                  value={detalleTemp.precio_unitario}
                  onChange={(e) => setDetalleTemp({...detalleTemp, precio_unitario: e.target.value})}
                  placeholder="0.00"
                  readOnly={formData.tipo_movimiento === 'salida'}
                  style={formData.tipo_movimiento === 'salida' ? {backgroundColor: '#f3f4f6', cursor: 'not-allowed'} : {}}
                  title={formData.tipo_movimiento === 'salida' ? 'Precio Promedio Ponderado (se autocompleta)' : ''}
                />
              </div>

              {/* Campos de presupuesto solo para salidas */}
              {formData.tipo_movimiento === 'salida' && (
                <>
                  <div className="form-group">
                    <label>Proyecto *</label>
                    <select
                      value={detalleTemp.proyecto}
                      onChange={(e) => handleProyectoChange(e.target.value)}
                      disabled={!!proyecto}
                    >
                      <option value="">Seleccionar...</option>
                      {proyectos.map(p => (
                        <option key={p.id} value={p.id}>{p.nombre}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Presupuesto de Costo *</label>
                    <select
                      value={detalleTemp.presupuesto_costo}
                      onChange={(e) => handlePresupuestoChange(e.target.value)}
                      disabled={!detalleTemp.proyecto}
                    >
                      <option value="">Seleccionar...</option>
                      {presupuestos.map(p => (
                        <option key={p.id} value={p.id}>{p.nombre}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Área *</label>
                    <select
                      value={detalleTemp.area}
                      onChange={(e) => handleAreaChange(e.target.value)}
                      disabled={!detalleTemp.presupuesto_costo}
                    >
                      <option value="">Seleccionar...</option>
                      {areas.map(a => (
                        <option key={a.id} value={a.id}>{a.nombre}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Partida *</label>
                    <select
                      value={detalleTemp.partida}
                      onChange={(e) => handlePartidaChange(e.target.value)}
                      disabled={!detalleTemp.area}
                    >
                      <option value="">Seleccionar...</option>
                      {partidas.map(p => (
                        <option key={p.id} value={p.id}>{p.nombre}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Recurso Presupuestario *</label>
                    <select
                      value={detalleTemp.presupuesto_costo_recurso}
                      onChange={(e) => setDetalleTemp({...detalleTemp, presupuesto_costo_recurso: e.target.value})}
                      disabled={!detalleTemp.partida}
                    >
                      <option value="">Seleccionar...</option>
                      {recursosPresupuestarios.map(r => (
                        <option key={r.id} value={r.id}>{r.nombre}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="form-group" style={{gridColumn: '1 / -1'}}>
                <label>Observaciones del Item</label>
                <input
                  type="text"
                  value={detalleTemp.observaciones}
                  onChange={(e) => setDetalleTemp({...detalleTemp, observaciones: e.target.value})}
                  placeholder="Observaciones específicas de este recurso"
                />
              </div>

              <div className="form-group" style={{gridColumn: '1 / -1', display: 'flex', gap: '1rem'}}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={agregarDetalle}
                >
                  {editingDetalleIndex !== null ? '✓ Actualizar Recurso' : '➕ Agregar Recurso'}
                </button>
                {editingDetalleIndex !== null && (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={cancelarEdicion}
                    style={{background: '#fee2e2', color: '#991b1b'}}
                  >
                    ✕ Cancelar
                  </button>
                )}
              </div>
            </div>

            {/* Lista de recursos agregados */}
            {detalles.length > 0 && (
              <div className="detalles-list">
                <h4>Recursos Agregados ({detalles.length})</h4>
                <table className="detalles-table">
                  <thead>
                    <tr>
                      <th>Recurso</th>
                      <th>Cantidad</th>
                      <th>Precio Unit.</th>
                      <th>Subtotal</th>
                      {formData.tipo_movimiento === 'salida' && (
                        <>
                          <th>Proyecto</th>
                          <th>Recurso Presupuestario</th>
                        </>
                      )}
                      <th>Observaciones</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalles.map((det, idx) => (
                      <tr key={idx}>
                        <td>{det.recurso_nombre}</td>
                        <td>{parseFloat(det.cantidad).toFixed(2)} {det.recurso_unidad}</td>
                        <td>${parseFloat(det.precio_unitario || 0).toLocaleString()}</td>
                        <td>${(parseFloat(det.cantidad) * parseFloat(det.precio_unitario || 0)).toLocaleString()}</td>
                        {formData.tipo_movimiento === 'salida' && (
                          <>
                            <td>{det.proyecto_nombre || '-'}</td>
                            <td>{det.presupuesto_costo_recurso_nombre || '-'}</td>
                          </>
                        )}
                        <td>{det.observaciones || '-'}</td>
                        <td>
                          <div style={{display: 'flex', gap: '0.5rem', justifyContent: 'center'}}>
                            <button
                              type="button"
                              className="btn-edit-small"
                              onClick={() => editarDetalle(idx)}
                              title="Editar"
                            >
                              ✏️
                            </button>
                            <button
                              type="button"
                              className="btn-delete-small"
                              onClick={() => eliminarDetalle(idx)}
                              title="Eliminar"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={loading || detalles.length === 0}>
              {loading ? 'Guardando...' : `${modoEdicion ? 'Actualizar' : 'Registrar'} Movimiento (${detalles.length} recurso${detalles.length !== 1 ? 's' : ''})`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CrearMovimientoBodegaModal;
